// アニメ調エフェクトのスプライト読み込み (ChatGPT 生成・黒背景の光物)。
// 黒背景なので additive (globalCompositeOperation='lighter') で重ねれば黒は消えて光だけ乗る。
// ブラウザ専用 (Vitest からは import しない)。
const PARRY_FLASH_COUNT = 4
const parryFlash: (HTMLImageElement | null)[] = new Array(PARRY_FLASH_COUNT).fill(null)
let parryLoaded = 0

/** パリィ閃光スプライト4枚の読み込み開始。 */
export function preloadParryFlashes(prefix = '/sprites/fx/'): void {
  if (typeof Image === 'undefined') return
  for (let i = 0; i < PARRY_FLASH_COUNT; i += 1) {
    if (parryFlash[i]) continue
    const img = new Image()
    img.onload = () => { parryFlash[i] = img; parryLoaded += 1 }
    img.src = `${prefix}parry_flash_${i}.png`
  }
}

/** index 番のパリィ閃光画像 (未読み込みなら null)。 */
export function getParryFlash(index: number): HTMLImageElement | null {
  return parryFlash[((index % PARRY_FLASH_COUNT) + PARRY_FLASH_COUNT) % PARRY_FLASH_COUNT]
}

export function parryFlashCount(): number { return PARRY_FLASH_COUNT }
export function parryFlashesReady(): boolean { return parryLoaded >= PARRY_FLASH_COUNT }

// 致命・撤破の FX スプライト (黒背景・additive)。
// ripple_execute = 致命着弾の冷青/紫の波紋 (2026-06-24 art-dept #FX2 で sprite 起こし。
//   旧 drawRipple の clean ring gradient を sprite+additive 経路へリプレース)。
const finisher: Record<string, HTMLImageElement | null> = {
  sigil: null, impact: null, soul: null, absorb: null, ash: null, ripple_execute: null,
  // 2026-10-11 purple-soul-blue: 紫の sigil/impact/ash を青白へ回した別名 (scripts/make_finisher_soulblue.py)。
  //   render.ts は *_soulblue を参照する。 旧の紫へ戻す時は getFinisherFx('sigil') などへ参照を戻す (元の絵は残してある)。
  sigil_soulblue: null, impact_soulblue: null, ash_soulblue: null,
}

/** 致命FXスプライトの読み込み開始。 */
export function preloadFinisherFx(prefix = '/sprites/fx/finisher/'): void {
  if (typeof Image === 'undefined') return
  for (const name of Object.keys(finisher)) {
    if (finisher[name]) continue
    const img = new Image()
    img.onload = () => { finisher[name] = img }
    img.src = `${prefix}${name}.png`
  }
}

/** 致命FXスプライト (未読み込みなら null)。 */
export function getFinisherFx(name: string): HTMLImageElement | null {
  return finisher[name] ?? null
}

// lanceButt 床隆起スプライト (通常背景・石畳が割れて盛り上がる)。
let lanceButtEruption: HTMLImageElement | null = null

/** lanceButt 床隆起スプライトの読み込み開始。 */
export function preloadLanceButtEruption(prefix = '/sprites/fx/'): void {
  if (typeof Image === 'undefined') return
  if (lanceButtEruption) return
  const img = new Image()
  img.onload = () => { lanceButtEruption = img }
  img.src = `${prefix}lanceButt_floor_eruption.png`
}

/** lanceButt 床隆起スプライト (未読み込みなら null)。 */
export function getLanceButtEruption(): HTMLImageElement | null {
  return lanceButtEruption
}

// 花嫁 床ハザードスプライト (2026-07-01 v3 rev2): 透過 PNG (RGBA) + source-over。
//   poison_mist_v3 = telegraph 予告 (veil/thorns 攻撃発生前に湧く呪詛の毒霧)
//   veil_stream_v3 = veil active (布が範囲を静的に敷き + waveX で光の走り・veilInvitation 以外の
//     kind:'veil' [剣舞 hemSweep / 先代の姫 curtainSweep] が使う)
//   thorn_growth_v3 = thorns active (rise で下から生える)
//   fxlab v3 rev2 の drawHazardSpriteCentered と同じ描画方式へ移行 (旧: 半透明壁+太枠/fillRect帯/lineToジグザグ)。
//   ※ standing_veil (立ち上がる裂けヴェールのPNG全面scale) は 2026-07-10 Codex監査回答で廃止・
//     ヴェールの招待は procedural-only (render.ts drawVeilWall) へ戻した。PNG自体は削除していない
//     (public/sprites/fx/hazard/standing_veil.png・未使用素材の整理は別チケット)。
const brideHazard: Record<string, HTMLImageElement | null> = {
  poison_mist: null, veil_stream: null, thorn_growth: null,
}

/** 花嫁床ハザードスプライトの読み込み開始。 */
export function preloadBrideHazard(prefix = '/sprites/fx/hazard/'): void {
  if (typeof Image === 'undefined') return
  const names: Array<'poison_mist' | 'veil_stream' | 'thorn_growth'> = ['poison_mist', 'veil_stream', 'thorn_growth']
  for (const name of names) {
    if (brideHazard[name]) continue
    const img = new Image()
    img.onload = () => { brideHazard[name] = img }
    img.src = `${prefix}${name}_v3.png`
  }
}

/** 花嫁床ハザードスプライト (未読み込みなら null)。 */
export function getBrideHazard(name: 'poison_mist' | 'veil_stream' | 'thorn_growth'): HTMLImageElement | null {
  return brideHazard[name]
}

// 覚醒王「大暴れ」の床ハザード = 湧き出る怨嗟の塊 (黒背景 + additive)。
//   2026-08-11 ユーザー選定。 候補A(湧き出る手)/C(呻く貌) は そざい/_inbox/2026-08-11-awoken-rampage-fx/rejected/ へ退避。
let rampageGrudge: HTMLImageElement | null = null

/** 覚醒王「大暴れ」床ハザードの読み込み開始。 */
export function preloadRampageGrudge(prefix = '/sprites/fx/hazard/'): void {
  if (typeof Image === 'undefined') return
  if (rampageGrudge) return
  const img = new Image()
  img.onload = () => { rampageGrudge = img }
  img.src = `${prefix}rampage_grudge_b.png`
}

// 大暴れ第5案の怨念 (2026-09-20)。床の帯と同じ画風で描き起こした塊を、役割ごとに3色。
//   紫=弾ける / 赤=弾けない / 黄緑=弾き返した。原画は頭が左・尾が右なので、飛ぶ向きへ回して使う。
const grudgeOrbs: Record<'violet' | 'red' | 'lime', HTMLImageElement | null> = { violet: null, red: null, lime: null }

/** 怨念の塊 3色の読み込み開始。 */
export function preloadGrudgeOrbs(prefix = '/sprites/fx/hazard/'): void {
  if (typeof Image === 'undefined') return
  for (const tone of ['violet', 'red', 'lime'] as const) {
    if (grudgeOrbs[tone]) continue
    const img = new Image()
    img.onload = () => { grudgeOrbs[tone] = img }
    img.src = `${prefix}grudge_orb_${tone}.webp`
  }
}

/** 怨念の塊 (未読み込みなら null)。 */
export function getGrudgeOrb(tone: 'violet' | 'red' | 'lime'): HTMLImageElement | null {
  return grudgeOrbs[tone]
}

// 怨念の6コマの絵 (2026-11-01 作者 art=sample → 型の答え ok で配布版)。紫は Codex の絵、赤・黄緑は同じ絵を今の1枚の色へ合わせて塗り替えた。
//   横一列に GRUDGE_SHEET_FRAMES コマ (1コマ 650×400)。どのコマも芯が同じ位置 (幅の 22.5%・高さの 53%) に来るよう揃えてある。
export const GRUDGE_SHEET_FRAMES = 6
const grudgeSheets: Partial<Record<'violet' | 'red' | 'lime', HTMLImageElement>> = {}
export function preloadGrudgeSheet(prefix = '/sprites/fx/hazard/'): void {
  if (typeof Image === 'undefined') return
  for (const tone of ['violet', 'red', 'lime'] as const) {
    if (grudgeSheets[tone]) continue
    const img = new Image()
    img.src = `${prefix}grudge_sheet_${tone}_1101.webp`
    grudgeSheets[tone] = img
  }
}
export function getGrudgeSheet(tone: 'violet' | 'red' | 'lime'): HTMLImageElement | null { return grudgeSheets[tone] ?? null }

// 大暴れ第5案の床技の割れ (2026-09-22 発注・票 docs/handoffs/2026-09-22-awoken-floor-wave-art-ticket.md)。
//   4コマ (0..2 = 走っている繰り返し / 3 = 生まれた瞬間)。いつも右へ走る向き。先端の足元 = (440, 300)。
const floorWave: (HTMLImageElement | null)[] = [null, null, null, null]

/** 床技の割れ 4コマの読み込み開始。 */
export function preloadFloorWave(prefix = '/sprites/fx/hazard/'): void {
  if (typeof Image === 'undefined') return
  for (let i = 0; i < 4; i++) {
    if (floorWave[i]) continue
    const img = new Image()
    img.onload = () => { floorWave[i] = img }
    img.src = `${prefix}floor_wave_${i}.webp`
  }
}

/** 床技の割れの i コマ目 (未読み込みなら null)。 */
export function getFloorWave(i: number): HTMLImageElement | null {
  return floorWave[i] ?? null
}

/** 覚醒王「大暴れ」床ハザード (未読み込みなら null)。 */
export function getRampageGrudge(): HTMLImageElement | null {
  return rampageGrudge
}
